"use client";

import { usePostHog } from "posthog-js/react";
import { useCallback, useState } from "react";

import { useHubspotContext } from "@/components/forms/hubspot_context";
import {
  getAdClickIdsFromUrl,
  resolveAppLinkAdClickIds,
} from "@/lib/ad-click-ids";
import type { ApplyNowEligibilityFields } from "@/lib/apply-now/submit-apply-now";
import { submitApplyNow } from "@/lib/apply-now/submit-apply-now";

export interface UseApplyNowSubmitOptions {
  eligibility?: ApplyNowEligibilityFields;
  redirectToSignup?: boolean;
  /** Allow submitting with no email — skips HubSpot, still redirects to signup. */
  allowEmptyEmail?: boolean;
  onSuccess?: () => void;
  onError?: (error: unknown) => void;
  /** Delay before signup redirect so UI can finish step handoff animations. */
  redirectDelayMs?: number;
}

function resolveRedirectDelayMs(delayMs?: number): number {
  if (!delayMs || delayMs <= 0) {
    return 0;
  }

  if (
    typeof window !== "undefined" &&
    window.matchMedia("(prefers-reduced-motion: reduce)").matches
  ) {
    return 0;
  }

  return delayMs;
}

export function useApplyNowSubmit(options: UseApplyNowSubmitOptions = {}) {
  const {
    eligibility,
    redirectToSignup = true,
    allowEmptyEmail = false,
    onSuccess,
    onError,
    redirectDelayMs,
  } = options;
  const hubspotContext = useHubspotContext();
  const posthog = usePostHog();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const submit = useCallback(
    async (email: string) => {
      setIsSubmitting(true);
      try {
        const urlClickIds = getAdClickIdsFromUrl();
        const adClickIds = resolveAppLinkAdClickIds(urlClickIds, (key) =>
          posthog.get_property?.(key)
        );
        await submitApplyNow({
          email,
          hubspotContext,
          eligibility,
          redirectToSignup,
          allowEmptyEmail,
          adClickIds,
          onComplete: onSuccess,
          onHubSpotError: (error) => {
            posthog.captureException(error, {
              "apply_now.redirect_to_signup": redirectToSignup,
              "http.route": "/api/submit-hubspot-form",
            });
          },
          redirectDelayMs: resolveRedirectDelayMs(redirectDelayMs),
          posthogHeaders: {
            "X-POSTHOG-SESSION-ID": posthog.get_session_id(),
            // @ts-expect-error - sessionManager is private but posthog said we can use it
            "X-POSTHOG-WINDOW-ID": posthog.sessionManager?._windowId as string,
            "X-POSTHOG-DISTINCT-ID": posthog.get_distinct_id(),
            "X-POSTHOG-GCLID":
              urlClickIds.gclid ??
              (posthog.get_property?.("gclid") as string) ??
              "",
            "X-POSTHOG-GBRAID":
              urlClickIds.gbraid ??
              (posthog.get_property?.("gbraid") as string) ??
              "",
            "X-POSTHOG-WBRAID":
              urlClickIds.wbraid ??
              (posthog.get_property?.("wbraid") as string) ??
              "",
          },
        });
      } catch (error) {
        onError?.(error);
        throw error;
      } finally {
        setIsSubmitting(false);
      }
    },
    [
      hubspotContext,
      eligibility,
      onError,
      onSuccess,
      redirectDelayMs,
      redirectToSignup,
      allowEmptyEmail,
      posthog,
    ]
  );

  return { submit, isSubmitting };
}
